import { formatApiError } from "@/lib/api";
import { useState } from "react";
import { Link, Navigate } from "react-router-dom";
import toast, { Toaster } from "react-hot-toast";
import { useAuth } from "@/lib/auth";
import SEO from "@/components/SEO";

// Role অনুযায়ী redirect destination — auth.jsx এর ROLE_HOME এর সাথে মিলিয়ে রাখো
const ROLE_HOME = {
    admin: "/admin",
    customer: "/profile",
};

export default function Login() {
    const { login, user, loading } = useAuth();
    const [form, setForm] = useState({ email: "", password: "" });
    const [showPass, setShowPass] = useState(false);
    const [busy, setBusy] = useState(false);

    // ইতিমধ্যে login করা থাকলে তার নিজের home এ পাঠাও
    if (!loading && user) {
        return <Navigate to={ROLE_HOME[user.role] ?? "/"} replace />;
    }

    const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

    const handleSubmit = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            // login() নিজেই role অনুযায়ী redirect করবে (auth.jsx এ window.location.href)
            await login(form.email.trim().toLowerCase(), form.password);
            toast.success("লগইন সফল");
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setBusy(false);
        }
    };

    return (
        <>
            <SEO title="Login" description="Sign in to your account - Self Preparation BD" />
            <div className="min-h-screen bg-hero-grad flex items-center justify-center px-4 relative overflow-hidden">
                <Toaster
                position="top-center"
                toastOptions={{
                    duration: 4000,
                    style: { borderRadius: "12px", fontWeight: "600", fontSize: "14px" },
                    success: {
                        style: { background: "#0B8A4A", color: "#fff" },
                        iconTheme: { primary: "#fff", secondary: "#0B8A4A" },
                    },
                    error: {
                        style: { background: "#D61F1F", color: "#fff" },
                        iconTheme: { primary: "#fff", secondary: "#D61F1F" },
                    },
                }}
            />

            <div className="w-full max-w-md z-10">
                <div className="text-center mb-8">
                    <h1 className="font-display text-3xl font-bold gradient-text-blue tracking-tight">
                        Welcome back
                    </h1>
                    <p className="text-sm text-slate-500 mt-1 font-medium">
                        Sign in to your account to continue
                    </p>
                </div>

                <div className="glass rounded-3xl shadow-pop p-8">
                    <form onSubmit={handleSubmit} className="space-y-5">
                        {/* Email */}
                        <div>
                            <label className="block text-sm font-semibold text-[#08145A] mb-1.5">
                                Email address
                            </label>
                            <div className="relative">
                                <span className="absolute inset-y-0 left-3.5 flex items-center text-slate-400 pointer-events-none">
                                    <svg width="17" height="17" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                                        <path d="M3 8l9 6 9-6M3 8v10a2 2 0 002 2h14a2 2 0 002-2V8M3 8a2 2 0 012-2h14a2 2 0 012 2" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                </span>
                                <input
                                    type="email"
                                    name="email"
                                    value={form.email}
                                    onChange={handleChange}
                                    required
                                    placeholder="you@example.com"
                                    className="w-full pl-10 pr-4 py-3 rounded-xl border border-[#D1DFF0] bg-white/80 text-[#08145A] placeholder-slate-400 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-[#0B1E8A]/30 focus:border-[#0B1E8A] transition"
                                />
                            </div>
                        </div>

                        {/* Password */}
                        <div>
                            <div className="flex justify-between items-center mb-1.5">
                                <label className="block text-sm font-semibold text-[#08145A]">Password</label>
                                <Link to="/forgot-password" className="text-xs text-[#0B1E8A] hover:text-[#D61F1F] font-semibold transition-colors">
                                    Forgot password?
                                </Link>
                            </div>
                            <div className="relative">
                                <span className="absolute inset-y-0 left-3.5 flex items-center text-slate-400 pointer-events-none">
                                    <svg width="17" height="17" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                                        <rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0110 0v4" strokeLinecap="round" />
                                    </svg>
                                </span>
                                <input
                                    type={showPass ? "text" : "password"}
                                    name="password"
                                    value={form.password}
                                    onChange={handleChange}
                                    required
                                    placeholder="••••••••"
                                    className="w-full pl-10 pr-11 py-3 rounded-xl border border-[#D1DFF0] bg-white/80 text-[#08145A] placeholder-slate-400 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-[#0B1E8A]/30 focus:border-[#0B1E8A] transition"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPass(!showPass)}
                                    className="absolute inset-y-0 right-3.5 flex items-center text-slate-400 hover:text-[#0B1E8A] transition-colors"
                                >
                                    {showPass ? (
                                        <svg width="17" height="17" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                                            <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24M1 1l22 22" strokeLinecap="round" strokeLinejoin="round" />
                                        </svg>
                                    ) : (
                                        <svg width="17" height="17" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                                            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" />
                                        </svg>
                                    )}
                                </button>
                            </div>
                        </div>

                        {/* Remember me */}
                        <div className="flex items-center gap-2">
                            <input type="checkbox" id="remember" className="w-4 h-4 rounded border-[#D1DFF0] accent-[#0B1E8A]" />
                            <label htmlFor="remember" className="text-sm text-slate-600 font-medium select-none">
                                Remember me for 30 days
                            </label>
                        </div>

                        {/* Submit */}
                        <button
                            type="submit"
                            disabled={busy}
                            className="w-full btn-primary py-3.5 rounded-xl font-bold text-sm tracking-wide shadow-soft disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                        >
                            {busy ? (
                                <>
                                    <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                                        <circle cx="12" cy="12" r="10" stroke="white" strokeWidth="3" strokeDasharray="60" strokeDashoffset="15" />
                                    </svg>
                                    Signing in…
                                </>
                            ) : (
                                "Sign In"
                            )}
                        </button>
                    </form>

                    <div className="flex items-center gap-3 my-5">
                        <div className="flex-1 h-px bg-[#E2EAF4]" />
                        <span className="text-xs text-slate-400 font-semibold uppercase tracking-widest">or</span>
                        <div className="flex-1 h-px bg-[#E2EAF4]" />
                    </div>

                    <p className="text-center text-sm text-slate-500 font-medium">
                        Don't have an account?{" "}
                        <Link to="/register" className="text-[#0B1E8A] font-bold hover:text-[#D61F1F] transition-colors">
                            Create one free
                        </Link>
                    </p>
                </div>
            </div>
        </div>
        </>
    );
}